import { supabase } from "@/integrations/supabase/client";
import type { ClientRecordFormConfig } from "@/lib/client-record-form";

const SUPPORTED = ["pt-PT", "pt-BR", "en", "es", "fr", "it"];

export const normalizeLocale = (input?: string | null): string => {
  const raw = (input || "").trim();
  if (SUPPORTED.includes(raw)) return raw;
  const base = raw.split("-")[0].toLowerCase();
  if (base === "pt") return "pt-PT";
  if (SUPPORTED.includes(base)) return base;
  return "pt-PT";
};

export interface TranslatedForm {
  config: ClientRecordFormConfig;
  /** translated option label -> original PT-PT value (data is always stored in PT-PT) */
  reverse: Record<string, string>;
}

const memCache = new Map<string, TranslatedForm>();

/**
 * Translates the (PT-PT authored) client record form config into the client's
 * language. Falls back to the original config on any failure.
 */
export const translateFormConfig = async (
  config: ClientRecordFormConfig,
  locale: string,
): Promise<TranslatedForm> => {
  const target = normalizeLocale(locale);
  if (target === "pt-PT") return { config, reverse: {} };

  const cacheKey = `${target}:${JSON.stringify(config).length}`;
  const cached = memCache.get(cacheKey);
  if (cached) return cached;

  const stored = sessionStorage.getItem(`ficha_i18n_${cacheKey}`);
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as TranslatedForm;
      memCache.set(cacheKey, parsed);
      return parsed;
    } catch { /* ignore */ }
  }

  // Collect every translatable string with a stable key
  const texts: { key: string; value: string }[] = [];
  const push = (key: string, value?: string | null) => {
    if (value && value.trim()) texts.push({ key, value });
  };
  push("intro", config.intro);
  push("consent_text", config.consent_text);
  config.sections.forEach((s, si) => {
    push(`s${si}_title`, s.title);
    s.fields.forEach((f, fi) => {
      push(`s${si}f${fi}_label`, f.label);
      push(`s${si}f${fi}_hint`, f.hint);
      push(`s${si}f${fi}_ph`, f.placeholder);
      (f.options || []).forEach((o, oi) => push(`s${si}f${fi}o${oi}`, o));
    });
  });

  try {
    const { data, error } = await supabase.functions.invoke("translate", {
      body: { texts, targetLang: target },
    });
    if (error || !data?.translated) return { config, reverse: {} };

    const map = new Map<string, string>();
    (data.translated as { key: string; value: string }[]).forEach((t) => map.set(t.key, t.value));
    const g = (key: string, fallback: string) => map.get(key) || fallback;

    const out: ClientRecordFormConfig = {
      intro: g("intro", config.intro),
      consent_text: g("consent_text", config.consent_text),
      sections: config.sections.map((s, si) => ({
        ...s,
        title: g(`s${si}_title`, s.title),
        fields: s.fields.map((f, fi) => ({
          ...f,
          label: g(`s${si}f${fi}_label`, f.label),
          hint: f.hint ? g(`s${si}f${fi}_hint`, f.hint) : f.hint,
          placeholder: f.placeholder ? g(`s${si}f${fi}_ph`, f.placeholder) : f.placeholder,
          options: f.options?.map((o, oi) => g(`s${si}f${fi}o${oi}`, o)),
        })),
      })),
    };

    const reverse: Record<string, string> = {};
    config.sections.forEach((s2, si) => {
      s2.fields.forEach((f, fi) => {
        (f.options || []).forEach((o, oi) => {
          const translated = map.get(`s${si}f${fi}o${oi}`);
          if (translated && translated !== o) reverse[translated] = o;
        });
      });
    });

    const result: TranslatedForm = { config: out, reverse };
    memCache.set(cacheKey, result);
    try { sessionStorage.setItem(`ficha_i18n_${cacheKey}`, JSON.stringify(result)); } catch { /* quota */ }
    return result;
  } catch (e) {
    console.warn("Form translation failed, keeping PT-PT", e);
    return { config, reverse: {} };
  }
};
